// API is reached through the Pages domain (`/api/*` proxied to the Worker).
// *.workers.dev POST bodies get RST by some ISPs/DPI (RF); *.pages.dev does not,
// and the web app then talks to its API same-origin (no CORS).
export const TEST_WORKER_URL = 'https://staffmanager-test-web.pages.dev/api';
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

export class TestWorkerCommandError extends Error {
  constructor(code, status, { retryable = false, cause, serverMessage = null } = {}) {
    super(code);
    this.name = 'TestWorkerCommandError';
    this.code = code;
    this.status = status;
    this.retryable = retryable;
    // Human-readable message from the server (BusinessInvariantError), when it
    // reads better than the bare code.
    this.serverMessage = serverMessage;
    if (cause) this.cause = cause;
  }
}

const delay = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

// Global "a command is running" signal so the UI can show a spinner instead of
// looking frozen. Every command client bumps this; background polls do not.
let busyCount = 0;
const busyListeners = new Set();
const emitBusy = () => { for (const fn of busyListeners) { try { fn(busyCount > 0); } catch { /* ignore */ } } };
export const commandBusy = {
  active: () => busyCount > 0,
  subscribe(fn) { busyListeners.add(fn); return () => busyListeners.delete(fn); },
  _begin() { busyCount += 1; if (busyCount === 1) emitBusy(); },
  _end() { busyCount = Math.max(0, busyCount - 1); if (busyCount === 0) emitBusy(); },
};

export function createTestWorkerCommandClient({
  fetchImplementation = fetch,
  sleep = delay,
  maxAttempts = 3,
  timeoutMs = 15000,
  workerUrl = TEST_WORKER_URL,
} = {}) {
  const inFlight = new Map();

  const requestOnce = async ({ user, path, body, idempotencyKey, forceRefreshToken }) => {
    const token = await user.getIdToken(forceRefreshToken);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetchImplementation(`${workerUrl}${path}`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
          'idempotency-key': idempotencyKey,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new TestWorkerCommandError(
          result?.error?.code || 'WORKER_REQUEST_FAILED',
          response.status,
          { retryable: RETRYABLE_STATUSES.has(response.status), serverMessage: result?.error?.message || null },
        );
      }
      return result?.data;
    } catch (error) {
      if (error instanceof TestWorkerCommandError) throw error;
      const timedOut = error?.name === 'AbortError';
      throw new TestWorkerCommandError(timedOut ? 'REQUEST_TIMEOUT' : 'NETWORK_UNAVAILABLE', 0, {
        retryable: true,
        cause: error,
      });
    } finally {
      clearTimeout(timeout);
    }
  };

  const execute = async request => {
    let forceRefreshToken = false;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await requestOnce({ ...request, forceRefreshToken });
      } catch (error) {
        if (error.status === 401 && !forceRefreshToken) {
          forceRefreshToken = true;
          continue;
        }
        if (!error.retryable || attempt === maxAttempts) throw error;
        await sleep(250 * 2 ** (attempt - 1));
      }
    }
    throw new TestWorkerCommandError('RETRY_EXHAUSTED', 0);
  };

  return Object.freeze({
    send(request) {
      if (!request?.idempotencyKey) throw new TypeError('idempotencyKey is required');
      const existing = inFlight.get(request.idempotencyKey);
      if (existing) return existing;
      commandBusy._begin();
      const operation = execute(request).finally(() => { inFlight.delete(request.idempotencyKey); commandBusy._end(); });
      inFlight.set(request.idempotencyKey, operation);
      return operation;
    },
    isInFlight: idempotencyKey => inFlight.has(idempotencyKey),
  });
}

const defaultClient = createTestWorkerCommandClient();
export const sendTestWorkerCommand = request => defaultClient.send(request);
